import type { EditionModele, VarianteDetaillee } from '@/types/api';

/**
 * La rédaction en cours de saisie. Chaque détail est un élément de liste, édité dans son
 * propre champ : un détail peut contenir des sauts de ligne (hérités des classeurs
 * sources), et l'ancienne saisie « une ligne = un détail » le découpait silencieusement en
 * plusieurs détails à l'enregistrement.
 */
export interface Redaction {
  intituleActivite: string;
  intituleCompetence: string;
  detailsActivite: string[];
  detailsCompetence: string[];
  /** Index 0..3 = niveaux 1..4 ; case vide = niveau non retenu. */
  niveaux: [string, string, string, string];
}

/** ACT_DET_1..9 / COMP_DET_1..9 : même plafond que la validation de l'API. */
export const MAX_DETAILS = 9;

export function redactionDepuis(v: VarianteDetaillee): Redaction {
  const niveaux: [string, string, string, string] = ['', '', '', ''];
  for (const n of v.niveauxMaitrise) {
    if (n.niveau >= 1 && n.niveau <= 4) niveaux[n.niveau - 1] = n.description;
  }
  return {
    intituleActivite: v.intituleActivite ?? '',
    intituleCompetence: v.intituleCompetence ?? '',
    detailsActivite: [...v.detailsActivite],
    detailsCompetence: [...v.detailsCompetence],
    niveaux,
  };
}

function elements(details: string[]): string[] {
  return details.map((d) => d.trim()).filter(Boolean);
}

/** Vrai si la saisie diffère de la rédaction de départ : sert à ne rien jeter sans prévenir. */
export function redactionModifiee(initiale: Redaction, courante: Redaction): boolean {
  return JSON.stringify(initiale) !== JSON.stringify(courante);
}

export function versEditionModele(redaction: Redaction): EditionModele {
  return {
    intituleActivite: redaction.intituleActivite.trim() || null,
    intituleCompetence: redaction.intituleCompetence.trim() || null,
    detailsActivite: elements(redaction.detailsActivite),
    detailsCompetence: elements(redaction.detailsCompetence),
    niveauxMaitrise: redaction.niveaux
      .map((description, i) => ({ niveau: i + 1, description: description.trim() }))
      .filter((n) => n.description.length > 0),
  };
}

interface Props {
  redaction: Redaction;
  onChange: (redaction: Redaction) => void;
  /** Préfixe des `id` : plusieurs formulaires cohabitent sur une même page. */
  idPrefix: string;
  desactive?: boolean;
  /**
   * Masque les niveaux de maîtrise. Ils ne sont plus renseignés sur les nouvelles
   * rédactions ; on ne les montre que là où une rédaction existante en porte déjà.
   */
  sansNiveaux?: boolean;
}

/** Une liste de détails : un champ par détail, ajout et retrait explicites. */
function ListeDetails({
  id,
  libelle,
  valeurs,
  desactive,
  onChange,
}: {
  id: string;
  libelle: string;
  valeurs: string[];
  desactive?: boolean;
  onChange: (valeurs: string[]) => void;
}) {
  return (
    <fieldset className="passerelles-champ">
      <legend>
        {libelle} ({valeurs.length}/{MAX_DETAILS})
      </legend>
      <div className="liste-edition">
        {valeurs.map((valeur, index) => (
          <div key={index} className="liste-edition__ligne">
            <textarea
              id={`${id}-${index}`}
              aria-label={`${libelle}, détail ${index + 1}`}
              className="edition__texte"
              rows={2}
              value={valeur}
              disabled={desactive}
              onChange={(e) => onChange(valeurs.map((v, i) => (i === index ? e.target.value : v)))}
            />
            <button
              type="button"
              className="bouton--retirer-ligne"
              onClick={() => onChange(valeurs.filter((_, i) => i !== index))}
              disabled={desactive}
            >
              Retirer
            </button>
          </div>
        ))}
      </div>
      {valeurs.length < MAX_DETAILS && (
        <button
          type="button"
          className="bouton--secondaire"
          onClick={() => onChange([...valeurs, ''])}
          disabled={desactive}
        >
          + Ajouter un détail
        </button>
      )}
    </fieldset>
  );
}

/**
 * Les champs d'une rédaction : intitulés, détails et niveaux de maîtrise. Les domaines de
 * connaissance n'y sont pas — ils pendent du couple et s'éditent métier par métier
 * (`EditeurConnaissances`).
 */
export function FormulaireRedaction({
  redaction,
  onChange,
  idPrefix,
  desactive,
  sansNiveaux,
}: Props) {
  return (
    <div className="edition-couple">
      <div className="passerelles-champ">
        <label htmlFor={`${idPrefix}-ia`}>Intitulé de l’activité</label>
        <input
          id={`${idPrefix}-ia`}
          type="text"
          className="edition__texte"
          value={redaction.intituleActivite}
          disabled={desactive}
          onChange={(e) => onChange({ ...redaction, intituleActivite: e.target.value })}
        />
      </div>
      <div className="passerelles-champ">
        <label htmlFor={`${idPrefix}-ic`}>Intitulé de la compétence</label>
        <input
          id={`${idPrefix}-ic`}
          type="text"
          className="edition__texte"
          value={redaction.intituleCompetence}
          disabled={desactive}
          onChange={(e) => onChange({ ...redaction, intituleCompetence: e.target.value })}
        />
      </div>

      <div className="edition-couple__details">
        <ListeDetails
          id={`${idPrefix}-da`}
          libelle="Détails de l’activité"
          valeurs={redaction.detailsActivite}
          desactive={desactive}
          onChange={(detailsActivite) => onChange({ ...redaction, detailsActivite })}
        />
        <ListeDetails
          id={`${idPrefix}-dc`}
          libelle="Détails de la compétence"
          valeurs={redaction.detailsCompetence}
          desactive={desactive}
          onChange={(detailsCompetence) => onChange({ ...redaction, detailsCompetence })}
        />
      </div>

      {!sansNiveaux && (
        <div className="passerelles-champ">
          <label>Niveaux de maîtrise (laisser vide si non retenu)</label>
          <div className="edition-couple__niveaux">
            {([0, 1, 2, 3] as const).map((i) => (
              <div key={i} className="passerelles-champ">
                <label htmlFor={`${idPrefix}-nm-${i}`} className="detail">
                  Niveau {i + 1}
                </label>
                <textarea
                  id={`${idPrefix}-nm-${i}`}
                  className="edition__texte"
                  rows={2}
                  value={redaction.niveaux[i]}
                  disabled={desactive}
                  onChange={(e) => {
                    const niveaux = [...redaction.niveaux] as Redaction['niveaux'];
                    niveaux[i] = e.target.value;
                    onChange({ ...redaction, niveaux });
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
